import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Twitter, Linkedin, Facebook, Instagram, Sparkles, Loader2, Calendar, Image as ImageIcon, Send, Trash2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { id: "twitter", label: "Twitter / X", icon: Twitter, color: "text-sky-500", bg: "bg-sky-50", limit: 280 },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "text-blue-700", bg: "bg-blue-50", limit: 3000 },
  { id: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-600", bg: "bg-blue-50", limit: 63206 },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "text-pink-600", bg: "bg-pink-50", limit: 2200 },
];

const trimForPlatform = (text, platformId) => {
  const p = PLATFORMS.find(x => x.id === platformId);
  if (!p) return text;
  return text.length <= p.limit ? text : text.slice(0, p.limit - 3) + "...";
};

export default function Social() {
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(["twitter", "linkedin"]);
  const [content, setContent] = useState("");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("engaging and conversational");
  const [scheduleAt, setScheduleAt] = useState("");
  const [image, setImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/social/accounts"), api.get("/social/posts")]);
    setAccounts(a.data);
    setPosts(p.data);
  };
  useEffect(() => { load(); }, []);

  const togglePlatform = (id) => {
    setSelected((s) => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const aiGenerate = async () => {
    if (!topic) { toast.error("Topic required"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/social-post", { topic, platforms: selected, tone });
      const first = data.posts?.[selected[0]] || data.posts?.twitter || Object.values(data.posts || {})[0] || "";
      const hashtags = (data.hashtags || []).map(h => h.startsWith("#") ? h : `#${h}`).join(" ");
      setContent([first, hashtags].filter(Boolean).join("\n\n"));
      toast.success("Post generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const aiImage = async () => {
    if (!topic && !content) { toast.error("Add a topic or content first"); return; }
    setGeneratingImage(true);
    try {
      const { data } = await api.post("/ai/image", { prompt: `Bold, modern social media post image about: ${topic || content.slice(0, 120)}. Vibrant, eye-catching, no text overlays.` });
      setImage(data.image);
      toast.success("Image ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image gen failed"); }
    finally { setGeneratingImage(false); }
  };

  const publish = async () => {
    if (!content || selected.length === 0) { toast.error("Content + platforms required"); return; }
    await api.post("/social/posts", {
      platforms: selected,
      content,
      image_url: image,
      scheduled_at: scheduleAt || null,
      status: scheduleAt ? "scheduled" : "published",
    });
    setContent(""); setImage(null); setTopic(""); setScheduleAt("");
    toast.success(scheduleAt ? "Scheduled" : "Published");
    load();
  };

  const remove = async (id) => { await api.delete(`/social/posts/${id}`); load(); };

  return (
    <div className="p-6 space-y-6" data-testid="social-page">
      {/* Page Header */}
      <div className="rise flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Social Studio</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Write once. Post everywhere. AI handles the variations.</p>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total posts", value: posts.length, color: "var(--brand)" },
          { label: "Scheduled", value: posts.filter(p => p.status === "scheduled").length, color: "#D97706" },
          { label: "Published", value: posts.filter(p => p.status === "published").length, color: "var(--success)" },
        ].map(({ label, value, color }) => (
          <Card key={label} className="rise card-premium p-4 flex items-center gap-3"
            style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="text-2xl font-bold" style={{ color, fontFamily: "var(--font-sans)" }}>{value}</div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-soft)" }}>{label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3 p-6 card-premium space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div>
            <Label>Platforms</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                const on = selected.includes(p.id);
                return (
                  <button
                    key={p.id}
                    data-testid={`platform-${p.id}`}
                    onClick={() => togglePlatform(p.id)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-all"
                    style={{
                      background: on ? "var(--brand-dim)" : "var(--bg)",
                      borderColor: on ? "var(--brand)" : "var(--border)",
                      color: on ? "var(--brand)" : "var(--text-soft)"
                    }}
                  >
                    <Icon className="w-4 h-4" /> {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Content</Label>
            <Textarea data-testid="social-content" value={content} onChange={(e) => setContent(e.target.value)} rows={6} placeholder="What's the message?" />
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs mono" style={{ color: content.length > 280 ? "var(--warn)" : "var(--text-soft)" }}>{content.length} chars</span>
              {content && selected.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {selected.map(pid => {
                    const p = PLATFORMS.find(x => x.id === pid);
                    const Icon = p?.icon;
                    const trimmed = trimForPlatform(content, pid);
                    return (
                      <button key={pid} onClick={() => { navigator.clipboard.writeText(trimmed); toast.success(`Copied for ${p?.label} (${trimmed.length} chars)`); }}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs border hover:opacity-80 transition-opacity ${p?.bg} ${p?.color} border-current/20`}
                        title={`Copy optimised for ${p?.label} (${p?.limit} char limit)`}>
                        {Icon && <Icon className="w-3 h-3" />} <Copy className="w-3 h-3" /> {p?.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {image && (
            <div className="relative">
              <img src={image} alt="generated" className="rounded-md max-h-64 border border-slate-200" />
              <Button variant="outline" size="sm" onClick={() => setImage(null)} className="absolute top-2 right-2">Remove</Button>
            </div>
          )}

          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label>Schedule (optional)</Label>
              <Input data-testid="social-schedule" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
            </div>
            <Button data-testid="social-image-btn" onClick={aiImage} disabled={generatingImage} variant="outline" className="gap-2">
              {generatingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} AI Image
            </Button>
            <Button data-testid="social-publish-btn" onClick={publish} className="text-white gap-2" style={{ background: "var(--brand)" }}>
              <Send className="w-4 h-4" /> {scheduleAt ? "Schedule" : "Publish"}
            </Button>
          </div>
        </Card>

        <Card className="lg:col-span-2 p-6 card-premium space-y-3" style={{ background: "color-mix(in srgb, var(--brand) 6%, var(--surface))", borderColor: "color-mix(in srgb, var(--brand) 25%, var(--border))" }}>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: "var(--brand)" }} />
            <h3 className="font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>AI Post Creator</h3>
          </div>
          <div><Label>Topic / idea</Label><Input data-testid="social-ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Launching our new analytics dashboard" /></div>
          <div><Label>Tone</Label><Input data-testid="social-ai-tone" value={tone} onChange={(e) => setTone(e.target.value)} /></div>
          <Button data-testid="social-ai-generate" onClick={aiGenerate} disabled={generating} className="w-full text-white gap-2" style={{ background: "var(--brand)" }}>
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate Post
          </Button>
          <div className="pt-3 border-t" style={{ borderColor: "color-mix(in srgb, var(--brand) 25%, var(--border))" }}>
            <div className="text-xs uppercase tracking-wider font-semibold font-sans mb-2" style={{ color: "var(--text-soft)" }}>Connected accounts</div>
            <div className="space-y-2">
              {accounts.map((a) => {
                const p = PLATFORMS.find(x => x.id === a.platform);
                const Icon = p?.icon || Twitter;
                return (
                  <div key={a.id} className="flex items-center gap-2 text-sm">
                    <Icon className="w-4 h-4" style={{ color: "var(--brand)" }} />
                    <span className="font-medium" style={{ color: "var(--text)" }}>{a.handle}</span>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(22,163,74,0.12)", color: "var(--success)" }}>Connected</span>
                  </div>
                );
              })}
              {accounts.length === 0 && (
                <div className="text-xs" style={{ color: "var(--text-soft)" }} data-testid="no-social-accounts">
                  No accounts connected yet. <Link to="/settings?tab=integrations" className="underline" style={{ color: "var(--brand)" }}>Connect one in Settings →</Link>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Scheduled & Published</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((p, i) => (
            <Card key={p.id} data-testid={`social-post-${i}`}
              className="card-premium p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex gap-1 mb-2 items-center">
                {p.platforms.map((pl) => {
                  const meta = PLATFORMS.find(x => x.id === pl);
                  const Icon = meta?.icon || Twitter;
                  return <span key={pl} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "var(--brand-dim)" }}><Icon className="w-3.5 h-3.5" style={{ color: "var(--brand)" }} /></span>;
                })}
                <span className="ml-auto text-xs px-2 py-0.5 rounded-full"
                  style={{
                    background: p.status === "published" ? "rgba(22,163,74,0.12)" : p.status === "scheduled" ? "rgba(245,158,11,0.12)" : "var(--bg)",
                    color: p.status === "published" ? "var(--success)" : p.status === "scheduled" ? "#D97706" : "var(--text-soft)"
                  }}>{p.status}</span>
              </div>
              {p.image_url && <img src={p.image_url} alt="" className="rounded-xl mb-2 max-h-32 w-full object-cover" />}
              <div className="text-sm whitespace-pre-wrap line-clamp-4" style={{ color: "var(--text)" }}>{p.content}</div>
              {p.scheduled_at && <div className="text-xs mt-2 font-sans flex items-center gap-1" style={{ color: "var(--text-soft)" }}><Calendar className="w-3 h-3" />{new Date(p.scheduled_at).toLocaleString()}</div>}
              {p.published_at && p.status === "published" && (
                <div className="text-xs mt-1 flex items-center gap-1" style={{ color: "var(--success)" }}><CheckCircle2 className="w-3 h-3" /> Published {new Date(p.published_at).toLocaleString()}</div>
              )}
              <div className="flex items-center gap-1 mt-2">
                {p.platforms.map(pid => {
                  const trimmed = trimForPlatform(p.content, pid);
                  const meta = PLATFORMS.find(x => x.id === pid);
                  const Icon = meta?.icon || Twitter;
                  return <button key={pid} title={`Copy optimized for ${meta?.label}`} onClick={() => { navigator.clipboard.writeText(trimmed); toast.success(`Copied for ${meta?.label}`); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-all" style={{ background: "var(--brand-dim)" }}><Icon className="w-3.5 h-3.5" style={{ color: "var(--brand)" }} /></button>;
                })}
                <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="ml-auto">
                  <Trash2 className="w-4 h-4" style={{ color: "var(--text-xsoft)" }} />
                </Button>
              </div>
            </Card>
          ))}
          {posts.length === 0 && (
            <Card className="card-premium col-span-full p-16 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <Twitter className="w-12 h-12 mx-auto mb-4 opacity-20" style={{ color: "var(--text)" }} />
              <div className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>No posts yet</div>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>Compose your first post and publish or schedule it above.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
